import React, { useCallback, useEffect, useState } from 'react';
import { Col, Form, FormInstance, Input, Row, Select } from 'antd';
import { months } from 'works/legacy/lib/date-helper';

type Props = {
  form: FormInstance;
  name: string;
  label: string;
  required: boolean;
  initialValue?: string;
};

export function FormHealthDocSelectMonthDay({
  form,
  name,
  label,
  required,
  initialValue,
}: Props) {
  const [month, setMonth] = useState(
    initialValue && Number(initialValue.split('-')[0])
  );
  const [day, setDay] = useState(
    initialValue && Number(initialValue.split('-')[1])
  );
  const [monthDay, setMonthDay] = useState(initialValue);

  const handleChange = useCallback(
    (value: string, dateString: string) => {
      setMonthDay(dateString);
      form.setFieldsValue({ [value]: dateString });
    },
    [form]
  );

  useEffect(() => {
    if (month && day) {
      const twoDigitsMonth = numberToTwoDigits(month);
      const twoDigitsDay = numberToTwoDigits(day);
      handleChange(name, `${twoDigitsMonth}-${twoDigitsDay}`);
    } else {
      handleChange(name, '');
    }
  }, [handleChange, name, month, day, form]);

  const isVowel = (str: string) => {
    return str.match(/[aeiou]/i);
  };

  const numberToTwoDigits = (value: number) => {
    return `0${value}`.slice(-2);
  };

  const article = isVowel(label.toLowerCase().slice(0, 1)) ? 'an' : 'a';

  const monthOptions = months.map((value, index) => {
    return {
      value: index + 1,
      label: value.name,
    };
  });

  const daysOptions = [...Array(31).keys()].map((value) => {
    return {
      value: value + 1,
      label: value + 1,
    };
  });

  return (
    <>
      <div className="ant-form-item-label">
        <label
          className={required ? 'ant-form-item-required' : ''}
          htmlFor={name}
        >
          {label}
        </label>
      </div>
      <Row gutter={[16, 0]}>
        <Col span={12}>
          <Select
            id={`${name}Month`}
            size="large"
            options={monthOptions}
            placeholder="Select Month"
            value={month}
            onSelect={(value) => setMonth(value)}
            showSearch
            style={{ width: '100%' }}
            optionFilterProp="children"
            filterOption={(input, option) =>
              !!option?.label.toLowerCase().includes(input.toLowerCase())
            }
          />
        </Col>
        <Col span={12}>
          <Select
            id={`${name}Day`}
            size="large"
            options={daysOptions}
            placeholder="Select Day"
            value={day}
            style={{ width: '100%' }}
            onSelect={(_value) => setDay(_value)}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              !!`${option?.label}`.includes(`${input}`)
            }
          />
        </Col>
      </Row>
      <Form.Item
        className="hidden-input"
        name={name}
        rules={[
          {
            required,
            message: `Please enter ${article} ${label}`,
          },
          {
            validator: (_, value) => {
              if (value && !value.match(/^\d{2}-\d{2}$/)) {
                return Promise.reject('Please enter a valid date (MM-DD)');
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Input value={monthDay} />
      </Form.Item>
      <style jsx global>{`
        .hidden-input .ant-form-item-control .ant-form-item-control-input {
          display: none;
        }
      `}</style>
    </>
  );
}
